import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth, api } from './context/AuthContext';
import { toast } from 'sonner';
import { CartProvider } from './context/CartContext';
import { SiteProvider } from './context/SiteContext';
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import ProducersPage from './pages/ProducersPage';
import ProducerDetailPage from './pages/ProducerDetailPage';
import RegionsPage from './pages/RegionsPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import BecomeProducerPage from './pages/BecomeProducerPage';
import ProducerDashboard from './pages/ProducerDashboard';
import FavoritesPage from './pages/FavoritesPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminPaymentsDashboard from './pages/AdminPaymentsDashboard';
import SuperAdminAnalytics from './pages/SuperAdminAnalytics';
import LegalPage from './pages/LegalPage';
import LegalAcceptancePage from './pages/LegalAcceptancePage';
import AdminLegalPage from './pages/AdminLegalPage';
import AdminLegalAuditPage from './pages/AdminLegalAuditPage';
import VendorFinanceDashboard from './pages/VendorFinanceDashboard';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProfilePage from './pages/ProfilePage';

import './App.css';

// Role hierarchy for permission checks
const ROLE_HIERARCHY = {
  user: 1,
  producer: 2,
  admin: 3,
  super_admin: 4
};

const hasRequiredRole = (userRole, requiredRole) => {
  // Admin and super_admin can access admin routes
  if (requiredRole === 'admin') {
    return userRole === 'admin' || userRole === 'super_admin';
  }
  // Producer routes accessible by producer, admin, super_admin
  if (requiredRole === 'producer') {
    return ['producer', 'admin', 'super_admin'].includes(userRole);
  }
  // Exact match for other roles
  return userRole === requiredRole;
};

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole && !hasRequiredRole(user?.role, requiredRole)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const EmailVerificationBanner = () => {
  const { isAuthenticated, emailVerified, user } = useAuth();
  const [dismissed, setDismissed] = React.useState(false);
  const [resending, setResending] = React.useState(false);

  if (!isAuthenticated || emailVerified || dismissed) return null;

  const handleResend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification');
      toast.success('Email di verifica inviata! Controlla la casella di posta.');
      setDismissed(true);
    } catch {
      toast.error('Errore nell\'invio. Riprova tra qualche minuto.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-4 text-sm">
      <span className="text-amber-800">
        📧 Verifica il tuo indirizzo email per accedere a tutte le funzionalità.
      </span>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={handleResend}
          disabled={resending}
          className="text-amber-700 font-medium hover:underline disabled:opacity-50"
        >
          {resending ? 'Invio...' : 'Invia di nuovo'}
        </button>
        <button onClick={() => setDismissed(true)} className="text-amber-600 hover:text-amber-800">✕</button>
      </div>
    </div>
  );
};

const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <EmailVerificationBanner />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/prodotti" element={<Layout><ProductsPage /></Layout>} />
      <Route path="/prodotti/:slug" element={<Layout><ProductDetailPage /></Layout>} />
      <Route path="/produttori" element={<Layout><ProducersPage /></Layout>} />
      <Route path="/produttori/:slug" element={<Layout><ProducerDetailPage /></Layout>} />
      <Route path="/regioni" element={<Layout><RegionsPage /></Layout>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrati" element={<RegisterPage />} />
      <Route path="/password-dimenticata" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verifica-email" element={<VerifyEmailPage />} />
      <Route path="/carrello" element={<Layout><CartPage /></Layout>} />
      <Route path="/diventa-produttore" element={<Layout><BecomeProducerPage /></Layout>} />
      <Route path="/legal/:slug" element={<Layout><LegalPage /></Layout>} />
      <Route path="/accetta-termini" element={<LegalAcceptancePage />} />

      {/* Protected Routes */}
      <Route path="/checkout" element={
        <ProtectedRoute>
          <Layout><CheckoutPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ordini" element={
        <ProtectedRoute>
          <Layout><OrdersPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/ordini/:orderId" element={
        <ProtectedRoute>
          <Layout><OrderDetailPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/preferiti" element={
        <ProtectedRoute>
          <Layout><FavoritesPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/account" element={
        <ProtectedRoute>
          <Layout><ProfilePage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/produttore/dashboard" element={
        <ProtectedRoute requiredRole="producer">
          <Layout><ProducerDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/produttore/finanze" element={
        <ProtectedRoute requiredRole="producer">
          <Layout><VendorFinanceDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute requiredRole="admin">
          <Layout><AdminDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/pagamenti" element={
        <ProtectedRoute requiredRole="admin">
          <Layout><AdminPaymentsDashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute requiredRole="super_admin">
          <Layout><SuperAdminAnalytics /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/legal" element={
        <ProtectedRoute requiredRole="admin">
          <Layout><AdminLegalPage /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin/legal/audit" element={
        <ProtectedRoute requiredRole="admin">
          <Layout><AdminLegalAuditPage /></Layout>
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SiteProvider>
        <CartProvider>
          <AppRoutes />
          <Toaster 
            position="top-center" 
            richColors 
            closeButton
            toastOptions={{
              style: {
                fontFamily: 'Manrope, sans-serif'
              }
            }}
          />
        </CartProvider>
        </SiteProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
